import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { Form, Formik, FormikHelpers } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';

import Card from 'components/Account/Common/Card/Card';
import Button from 'components/Common/Button/Button';
import { useApiErrors } from 'components/Common/Formik/hooks/useApiErrors';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import { FLASH_MESSAGE_STATE_KEY } from 'contexts/flashMessageContext';
import useCurrentCountry from 'hooks/useCurrentCountry';
import validationSchema from 'schemas/AccountEditPassword';
import { fetcherWithBody } from 'services/fetcher';
import { apiUrls } from 'services/api-urls';
import { getRoutesUri } from 'services/generic';
import { GTM_FORM_CHANGE_PASSWORD } from 'settings/gtm/gtmForm';
import { GTM_PAGE_ACCOUNT } from 'settings/gtm/gtmPageView';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';

import styles from './ChangePassword.module.scss';

type FormErrorsResponse = {
  oldPassword?: string;
  newPassword?: string;
  confirmNewPassword?: string;
};

function ChangeEmail(): JSX.Element {
  const { t } = useTranslation(['personalDetailsPage']);
  const { isUk } = useCurrentCountry();
  const navigate = useNavigate();
  const [errorSubmitted, setErrorSubmitted] = useState(false);
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });

  const extraStyleField: ExtraStyleType = {
    container: styles.container,
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  const params = {
    password: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const { setApiErrors, clearApiErrors } = useApiErrors<typeof params>();

  function resetForm(actions: FormikHelpers<typeof params>) {
    setTimeout(() => {
      actions.resetForm();
    }, 1000);
  }

  async function onSubmit(
    values: typeof params,
    actions: FormikHelpers<typeof params>
  ) {
    clearApiErrors(actions);
    const response = await fetcherWithBody(
      apiUrls.getCustomerChangePassword(),
      {
        oldPassword: values.password,
        password: {
          newPassword: values.newPassword,
          confirmNewPassword: values.confirmNewPassword,
        },
      },
      true
    );

    if (response.ok) {
      navigate(getRoutesUri('customerAccountInformation') as string, {
        state: {
          [FLASH_MESSAGE_STATE_KEY]: {
            message: t('LABEL_PASSWORD_EDIT_SUCCESS'),
            severity: 'success',
          },
        },
      });

      return;
    }

    const { errors }: { errors?: FormErrorsResponse } = await response.json();

    if (errors) {
      setApiErrors(actions, {
        password: errors.oldPassword ?? '',
        newPassword: errors.newPassword ?? '',
        confirmNewPassword: errors.confirmNewPassword ?? '',
      });

      return;
    }

    setErrorSubmitted(true);
    resetForm(actions);
  }

  return (
    <Card classes={{ container: styles.container }}>
      <div className={styles.title}>
        <label>{t('LABEL_PAGE_TITLE')}</label>
      </div>
      <Formik
        initialStatus={{ apiErrors: {} }}
        initialValues={params}
        onSubmit={onSubmit}
        validationSchema={validationSchema(isUk, t)}
      >
        <Form>
          <FormikErrorsGtmLogger
            page_type={GTM_PAGE_ACCOUNT}
            form_type={GTM_FORM_CHANGE_PASSWORD}
          />
          <div className={styles.changePassword}>
            <FontAwesomeIcon icon={faChevronRight} />
            <label className={styles.labelEdit}>
              {t('LABEL_EDIT_PASSWORD')}
            </label>
          </div>
          <div className={styles.fields}>
            <TextFieldCustom
              label={t('LABEL_CURRENT_PASSWORD')}
              required
              extraStyle={extraStyleField}
              name="password"
              type="password"
              placeholder={
                !isMobileAndPortraitTablet
                  ? t('LABEL_PLACEHOLDER_CURRENT_PASSWORD')
                  : ''
              }
            />
            <TextFieldCustom
              id="PasswordWithHelper"
              label={t('LABEL_NEW_PASSWORD')}
              required
              extraStyle={extraStyleField}
              name="newPassword"
              type="password"
              placeholder={
                !isMobileAndPortraitTablet
                  ? t('LABEL_PLACEHOLDER_NEW_PASSWORD')
                  : ''
              }
            />
            <TextFieldCustom
              label={t('LABEL_CONFIRM_NEW_PASSWORD')}
              required
              extraStyle={extraStyleField}
              name="confirmNewPassword"
              type="password"
              placeholder={
                !isMobileAndPortraitTablet
                  ? t('LABEL_PLACEHOLDER_NEW_PASSWORD_AGAIN')
                  : ''
              }
            />
            {errorSubmitted && (
              <label className={styles.formError}>
                {t('LABEL_PERSONAL_DETAILS_EDITED_ERROR')}
              </label>
            )}
            <div className={styles.button}>
              <Button>{t('LABEL_BUTTON')}</Button>
            </div>
          </div>
          <RequiredFields classes={{ wrapper: styles.requiredFieldsInfo }} />
        </Form>
      </Formik>
    </Card>
  );
}

export default ChangeEmail;
